import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CompanyContactsModalComponent } from '@src/app/features/dashboard/company/company-contacts/company-contacts-modal.component';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import {
  TableAction,
  TableActions,
  TableComponent,
} from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { User } from '@interfaces/a-organizar/user';
import { Company } from '@interfaces/common/company';
import { Pagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { OnboardingService } from '@services/http/onboarding.service';
import { UserService } from '@services/http/user.service';
import { Roles } from '@src/app/core/guard/Roles';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { PermissionsService } from '@src/app/core/services/helpers/permissions.service';
import { UserDataAction } from '@src/app/shared/components/user-data-h/user-data-h.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { first } from 'rxjs/operators';
import { ModalBankContactsComponent } from '@src/app/features/dashboard/market/banking-relationship/components/modal-bank-contacts/modal-bank-contacts.component';
import { OnboardingV2ViewModalComponent } from '@src/app/features/onboarding/v2/modals/onboarding-v2-view-modal/onboarding-v2-view-modal.component';

@Component({
  selector: 'app-company-list-content',
  templateUrl: './company-list-content.component.html',
  styleUrls: ['./company-list-content.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class CompanyListContentComponent extends VaultBaseComponent implements OnInit {
  @Input() groupList: Company[] = [];
  /** Falso até a primeira resposta da listagem: mostra o esqueleto em vez de "Nenhuma empresa encontrada". */
  @Input() loaded = true;
  @Input() pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 10,
  };
  /** Mostra os checkboxes da tabela (troca de setor em lote, card 3642: só com `COMPANYSECTOR_ASSIGN`). */
  @Input() selectable = false;

  /** Empresas marcadas na tabela, a cada mudança (card 3642). */
  @Output() selectionChange = new EventEmitter<Company[]>();
  @Output() sendTablePagination: EventEmitter<PaginatorState> = new EventEmitter<PaginatorState>();
  @Output() sendTableAction: EventEmitter<TableActions<Company>> = new EventEmitter<
    TableActions<Company>
  >();

  customerPermissions = Permissions.Company;
  columnDefinitions: ColumnDefinition[] = [];
  tableOptions: MenuItem[] = [];
  users: User[] = [];
  selectedCompany: Company | null = null;
  selectedUser: User | null = null;
  isAdmin = false;

  constructor(
    translateService: TranslateService,
    store: Store<AppState>,
    private toast: ToastHelperService,
    private router: Router,
    private dialogService: ConfirmDialogService,
    private confirmationService: ConfirmationService,
    private userService: UserService,
    private onboardingService: OnboardingService,
    private utilsService: UtilsService,
    private permissionsService: PermissionsService
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    // Verificar se usuário é ADMIN - usar first() para evitar memory leak
    this.permissionsService
      .hasRole(Roles.ADMIN)
      .pipe(first())
      .subscribe(isAdmin => {
        this.isAdmin = isAdmin;
      });

    this.tableOptions = [
      {
        label: this.t(this.tk.User.AddUser),
        icon: 'pi pi-user-plus',
        action: 'add-user',
      },
      {
        label: this.t(this.tk.General.Update),
        icon: 'pi pi-pencil',
        action: 'edit',
      },
      {
        label: this.t(this.tk.General.Delete),
        icon: 'pi pi-trash',
        action: 'delete',
      },
      {
        label: this.t(this.tk.Onboarding.SendOnboarding),
        icon: 'pi pi-send',
        action: 'send-onboarding',
      },
      {
        label: this.t(this.tk.Onboarding.DoOnboarding),
        icon: 'pi pi-check-circle',
        action: 'do-onboarding',
      },
      {
        label: this.t(this.tk.Management.DocumentAnalysis1),
        icon: 'pi pi-file',
        action: 'do-document-analysis',
      },
      {
        label: this.t(this.tk.General.Users),
        icon: 'pi pi-users',
        action: 'users',
      },
      {
        label: this.t(this.tk.Management.DataRoom),
        icon: 'pi pi-folder-open',
        action: 'dataroom',
      },
      {
        label: 'Upload template B.C',
        icon: 'pi pi-upload',
        action: 'upload-business-case',
      },
      {
        label: 'Editar Business Case',
        icon: 'pi pi-file-edit',
        action: 'edit-business-case',
      },
      {
        label: 'Parâmetros de Faturamento',
        icon: 'pi pi-cog',
        action: 'invoicing-parameters',
      },
      {
        label: 'Contatos',
        icon: 'pi pi-users',
        action: 'company-contacts',
      },
      {
        label: 'Contatos Bancários',
        icon: 'pi pi-address-book',
        action: 'bank-contacts',
      },
      {
        label: 'Ver Onboarding',
        icon: 'pi pi-eye',
        action: 'view-onboarding',
      },
    ];
    this.columnDefinitions = [
      new ColumnDefinition({
        field: 'sequentialId',
        header: this.t(this.tk.Management.Id),
        sortable: true,
      }),
      new ColumnDefinition({
        field: 'corporateName',
        header: this.t(this.tk.General.CorporateReason),
        sortable: true,
      }),
      new ColumnDefinition({
        field: 'cnpj',
        header: this.t(this.tk.General.CNPJ),
        sortable: true,
        format: 'cnpj',
      }),
      new ColumnDefinition({
        field: 'tradeName',
        header: this.t(this.tk.General.FantasyN),
        sortable: true,
      }),
      new ColumnDefinition({
        field: 'companySectorName',
        header: 'Setor',
        // O nome vem do cadastro de setores (navegação), não de coluna da empresa: ordenar por ele não é suportado.
        sortable: false,
      }),
      new ColumnDefinition({
        field: 'commercialStatus',
        header: 'Situação',
        // Derivada no backend a cada leitura (deal de parceiro sem empréstimo contratado): a
        // página só traz a situação das linhas dela, então ordenar por ela enganaria.
        sortable: false,
      }),
      new ColumnDefinition({
        field: 'commercialPartnerName',
        header: 'Parceiro',
      }),
      new ColumnDefinition({
        field: 'progress',
        header: this.t(this.tk.Onboarding.Progress),
        sortable: true,
      }),
    ];
  }

  @ViewChild('table', { static: false })
  table!: TableComponent<Company>;

  @ViewChild('modalCompanyContacts', { static: false })
  modalCompanyContacts!: CompanyContactsModalComponent;

  @ViewChild('modalBankContacts', { static: false })
  modalBankContacts!: ModalBankContactsComponent;

  @ViewChild('modalOnboardingView', { static: false })
  modalOnboardingView!: OnboardingV2ViewModalComponent;

  resetPagination() {
    if (this.table) this.table.resetPagination();
  }
  handleTableAction(tableAction: TableAction<Company>) {
    switch (tableAction.action) {
      case 'edit':
        this.router.navigate(['dashboard/company/register', tableAction.item.id]);
        break;
      case 'delete':
        this.handleDeleteAction(tableAction.item);
        break;
      case 'do-onboarding':
        this.router.navigate(['onboarding/company', tableAction.item.id]);
        break;
      case 'do-document-analysis':
        this.router.navigate(['dashboard/document-analysis-company/', tableAction.item.id]);
        break;
      case 'send-onboarding':
        this.handleSendingOnboarding(tableAction.item);
        break;
      case 'add-user':
        this.router.navigate([`dashboard/user/register/company`, tableAction.item.id]);
        break;
      case 'users':
        this.router.navigateByUrl(`/dashboard/user/list?companyId=${tableAction.item.id}`);
        break;
      case 'dataroom':
        // Rota do Data Room é company-folders/:id (o nome da empresa no caminho dava 404). O `?companyId=` vai junto:
        // no escopo "Todas as empresas" é dele que a tela tira a empresa (Bug 3797).
        this.router.navigate(['/dashboard/market/data-room/company-folders', tableAction.item.id], {
          queryParams: { companyId: tableAction.item.id },
        });
        break;
      case 'upload-business-case':
        this.sendTableAction.emit({ action: 'upload-business-case', items: [tableAction.item] });
        break;
      case 'edit-business-case':
        this.router.navigateByUrl(`/dashboard/business-case?companyId=${tableAction.item.id}`);
        break;
      case 'invoicing-parameters':
        this.sendTableAction.emit({ action: 'invoicing-parameters', items: [tableAction.item] });
        break;
      case 'company-contacts':
        this.modalCompanyContacts.open(tableAction.item.id);
        break;
      case 'bank-contacts':
        this.modalBankContacts.open(tableAction.item.id);
        break;
      case 'view-onboarding':
        this.modalOnboardingView.open(tableAction.item.id!);
        break;
    }
  }

  addCompany() {
    this.router.navigate(['dashboard/company/register']);
  }

  handleTablePagination(pagination: PaginatorState) {
    this.sendTablePagination.emit(pagination);
  }

  handleSendingOnboarding(company: Company) {
    this.selectedCompany = company;
    this.utilsService.setIsLoading(true);

    this.userService.getUsersList(null, null, { companyId: company.id }).subscribe({
      next: response => {
        this.users = response?.data || [];
        this.utilsService.setIsLoading(false);

        this.dialogService.confirm(
          '',
          this.t(this.tk.Onboarding.ChooseUserToSendOnboardingLink),
          '',
          '',
          '',
          'select-user-to-onboarding',
          false,
          false
        );
      },
      error: error => {
        console.error('getUsersList error: ', error);
        this.utilsService.setIsLoading(false);
      },
    });
  }

  /** Desmarca todas as linhas (depois da troca de setor em lote ou pelo X do menu flutuante). */
  clearSelection(): void {
    this.table?.clearSelection();
  }

  handleTableSelection({ action, items }: TableActions<Company>) {
    switch (action) {
      case 'selection-change':
        this.selectionChange.emit(items ?? []);
        break;
      case 'delete':
        this.confirmationService
          .confirm(
            this.t(this.tk.Management.CompanyDeleteConfirmationTitle),
            this.t(this.tk.Management.CompanyDeleteConfirmationDescription)
          )
          .subscribe(confirmed => {
            if (confirmed) {
              this.sendTableAction.emit({ action, items });
            }
          });
        break;
    }
  }

  handleDeleteAction(item: Company) {
    this.confirmationService
      .confirm(
        this.t(this.tk.Management.CompanyDeleteConfirmationTitle),
        this.t(this.tk.Management.CompanyDeleteConfirmationDescription)
      )
      .subscribe(confirmed => {
        if (confirmed) {
          this.sendTableAction.emit({ action: 'delete', items: [item] });
        }
      });
  }

  handleSendAction(action: UserDataAction, user: User): void {
    this.selectedUser = user;
    this.onboardingService
      .postCompanySendMainEmailOnboarding(this.selectedCompany?.id as string, user.id)
      .subscribe({
        next: () => {
          this.dialogService.close();

          this.dialogService.confirm(
            this.t(this.tk.Onboarding.invitationToUserByCompanySuccess, {
              email: user.email,
            }),
            this.t(this.tk.Onboarding.registrationCompleted),
            this.t(this.tk.General.Ok),
            '',
            '',
            'user-to-onboarding-success',
            false,
            true
          );
        },
        error: error => {
          console.error('postCompanySendMainEmailOnboarding error: ', error);
          this.toast.showToastError(
            this.t(this.tk.Onboarding.ErrorSendingOnboardingLink),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  getMenuActions(company: Company): MenuItem[] {
    const actions: MenuItem[] = [
      {
        label: this.t(this.tk.User.AddUser),
        icon: 'pi pi-user-plus',
        action: 'add-user',
        command: () => {
          this.handleTableAction({ action: 'add-user', item: company });
        },
      },
      {
        label: this.t(this.tk.General.Update),
        icon: 'pi pi-pencil',
        action: 'edit',
        command: () => {
          this.handleTableAction({ action: 'edit', item: company });
        },
      },
      {
        label: this.t(this.tk.General.Delete),
        icon: 'pi pi-trash',
        action: 'delete',
        command: () => {
          this.handleTableAction({ action: 'delete', item: company });
        },
      },
      {
        label: this.t(this.tk.Onboarding.SendOnboarding),
        icon: 'pi pi-send',
        action: 'send-onboarding',
        command: () => {
          this.handleTableAction({ action: 'send-onboarding', item: company });
        },
      },
      {
        label: this.t(this.tk.Onboarding.DoOnboarding),
        icon: 'pi pi-check-circle',
        action: 'do-onboarding',
        command: () => {
          this.handleTableAction({ action: 'do-onboarding', item: company });
        },
      },
      {
        label: this.t(this.tk.Management.DocumentAnalysis1),
        icon: 'pi pi-file',
        action: 'do-document-analysis',
        command: () => {
          this.handleTableAction({ action: 'do-document-analysis', item: company });
        },
      },
      {
        label: this.t(this.tk.General.Users),
        icon: 'pi pi-users',
        action: 'users',
        command: () => {
          this.handleTableAction({ action: 'users', item: company });
        },
      },
      {
        label: this.t(this.tk.Management.DataRoom),
        icon: 'pi pi-folder-open',
        action: 'dataroom',
        command: () => {
          this.handleTableAction({ action: 'dataroom', item: company });
        },
      },
      {
        label: 'Upload template B.C',
        icon: 'pi pi-upload',
        action: 'upload-business-case',
        command: () => {
          this.handleTableAction({ action: 'upload-business-case', item: company });
        },
      },
      {
        label: 'Editar Business Case',
        icon: 'pi pi-file-edit',
        action: 'edit-business-case',
        command: () => {
          this.handleTableAction({ action: 'edit-business-case', item: company });
        },
      },
      {
        label: 'Parâmetros de Faturamento',
        icon: 'pi pi-cog',
        action: 'invoicing-parameters',
        command: () => {
          this.handleTableAction({ action: 'invoicing-parameters', item: company });
        },
      },
      {
        label: 'Contatos',
        icon: 'pi pi-users',
        action: 'company-contacts',
        command: () => {
          this.handleTableAction({ action: 'company-contacts', item: company });
        },
      },
      {
        label: 'Contatos Bancários',
        icon: 'pi pi-address-book',
        action: 'bank-contacts',
        command: () => {
          this.handleTableAction({ action: 'bank-contacts', item: company });
        },
      },
      {
        label: 'Ver Onboarding',
        icon: 'pi pi-eye',
        action: 'view-onboarding',
        command: () => {
          this.handleTableAction({ action: 'view-onboarding', item: company });
        },
      },
    ];

    // Adicionar ação de transferência apenas para ADMIN
    if (this.isAdmin) {
      actions.push({
        label: 'Transferir para outro tenant',
        icon: 'pi pi-share-alt',
        action: 'transfer-company',
        command: () => {
          this.sendTableAction.emit({ action: 'transfer-company', items: [company] });
        },
      });
    }

    return actions;
  }

  get formatedGroupList(): Company[] {
    const formated = this.groupList.map((group: Company) => {
      return group;
    });
    return formated;
  }
}
